
import {translate} from '@elements/translations';
import {getLanguageConfig, getSkiTicketBaseConfig, getActivityBaseConfig, getPriceCalculatorBaseConfig} from "../../utils/config-helper";
import {addParamsToUrl, assertData, fetcher, isUndefined, addDaysISO} from "../../utils/utils";
import {DateStruct, ActivityDateStruct, PriceCalculatorDateStruct} from "../../utils/structs";
import CalendarDate from "./CalendarDate.vue";
import {ref, computed, watch, reactive, onMounted, defineComponent, defineEmits} from 'vue';
import { storeToRefs } from 'pinia'
import { useCalendarStore } from '../../stores/Calendar';
import { useActivityStore } from '../../stores/ActivityStore';
import LoadingSpinner from "../loading/LoadingSpinner.vue";

export default defineComponent({
    name: 'Calendar',
    components: {
        CalendarDate,
        LoadingSpinner
    },
    props: {
        errorMode: {
            type: Boolean,
            required: false,
        },
        ticketId: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            required: false,
            default: "ticket"
        },
        selectedDateStart: {
            type: String
        },
        selectedDateEnd: {
            type: String
        },
        isTimeList: {
            type: Boolean,
            required: false,
            default: false
        },
        selectedInterestIDs: { // just for pricecalculator refetching
            type: Array,
            required: false,
            default: []
        },
        selectedCategoryIDs: { // just for pricecalculator refetching
            type: Array,
            required: false,
            default: []
        },
        selectedPriceGroupIDs: { // just for pricecalculator refetching
            type: Array,
            required: false,
            default: []
        },
        hideTendency: {
            type: Boolean,
            required: false,
            default: false
        },
        hideSeason: {
            type: Boolean,
            required: false,
            default: false
        },
    },
    emits: ['changeDateStart', 'changeDateEnd', 'minSelectableDates', 'changeCalendarConfig'],
    setup(props, {emit}) {
        const dateConfig = ref<object>({});
        const calendarConfig = ref<object>({});
        const minDate = ref<string>({});
        const maxDate = ref<string>({});
        const error = ref<string>(null);
        const calendarStore = useCalendarStore();
        const { shownStart, shownEnd } = storeToRefs(calendarStore);
        const { changeShownStart, changeShownEnd } = calendarStore;
        const isLoading = ref<boolean>(true);

        const isMobile = matchMedia('(max-width: 767px)').matches;
        const isActivity = props.type === "activity";

        const fetchDates = async (setInitialData: Boolean) => {
            const params = {
                ticketId: props.ticketId,
                isMobile: isMobile,
                ...(shownStart.value.year !== undefined && {shownStartYear: shownStart.value.year}),
                ...(shownStart.value.month !== undefined && {shownStartMonth: shownStart.value.month}),
                ...(shownEnd.value.year !== undefined && {shownEndYear: shownEnd.value.year}),
                ...(shownEnd.value.month !== undefined && {shownEndMonth: shownEnd.value.month})
            };

            if(props.type === 'pricecalculator') {
                params.selectedInterestIDs = props.selectedInterestIDs;
                params.selectedCategoryIDs = props.selectedCategoryIDs;
                params.selectedPriceGroupIDs = props.selectedPriceGroupIDs;
            }

            let url = ''
            let assertDataWith = null

            switch(props.type) {
                case 'ticket':
                    url = getSkiTicketBaseConfig().calendar;
                    assertDataWith = DateStruct
                    break;
                case 'activity':
                    url = getActivityBaseConfig().calendar;
                    assertDataWith = ActivityDateStruct
                    break;
                case 'pricecalculator':
                    url = getPriceCalculatorBaseConfig().calendar;
                    assertDataWith = PriceCalculatorDateStruct
                    break;
                default:
                    url = getSkiTicketBaseConfig().calendar;
            }

            url = addParamsToUrl(url, new URLSearchParams(params));
            await fetcher(url).then((data) => {
                assertData(data, assertDataWith, 'Calendar', 'fetchDates', 'danger');

                dateConfig.value = data.calendarDates;
                calendarConfig.value = data.calendarConfiguration;
                minDate.value = data.calendarConfiguration.minDate;
                maxDate.value = data.calendarConfiguration.maxDate;
                emit('minSelectableDates', data.calendarConfiguration.minSelectableDates);
                emit('changeCalendarConfig', data.calendarConfiguration);
                if(setInitialData) {
                    changeShownStart(data.calendarDates[0].month, data.calendarDates[0].year);
                    if(!isMobile) {
                        changeShownEnd(data.calendarDates[1].month, data.calendarDates[1].year);
                    } else {
                        changeShownEnd(data.calendarDates[0].month, data.calendarDates[0].year);
                    }
                }
                isLoading.value = false;
            }).catch((error) => {
                console.log(error);
            });
        };

        const weekdays = computed<string[]>(() => {
            let weekdays = [];
            for (let i = 0; i < 7; i++) {
                let weekday = new Date(2021, 1, i + 1).toLocaleDateString(getLanguageConfig(), {weekday: 'long'});
                let weekdayMobile = new Date(2021, 1, i + 1).toLocaleDateString(getLanguageConfig(), {weekday: 'short'});
                weekdays.push({
                    desktop: weekday,
                    mobile: weekdayMobile
                });
            }
            return weekdays;
        });

        const prevDayCount = (firstDate: string): number => {
            let weekday = new Date(firstDate).toLocaleDateString(getLanguageConfig(), {weekday: 'long'});
            let weekdayIndex = +weekdays.value.findIndex((element) => element.desktop === weekday);
            return weekdayIndex;
        };

        const getMonthName = (month: number): string => {
            //get full name of month index
            return new Date(2021, +month -1, 1).toLocaleDateString(getLanguageConfig(), {month: 'long'});
        };

        const getMonthNumberOfDate = (date: string): number => {
            //get full name of month index
            return new Date(date).getMonth();
        };

        const prevShownCalendar = () => {
            const addMonth = isMobile ? 1 : 2;

            if(shownStart.value.month > 2 && !isMobile || shownStart.value.month > 1 && isMobile) {
                changeShownStart(shownStart.value.month - addMonth, shownStart.value.year);
            } else if(shownStart.value.month === 2 && !isMobile || shownStart.value.month === 1 && isMobile) {
                changeShownStart(12, shownStart.value.year - 1);
            } else {
                changeShownStart(11, shownStart.value.year - 1);
            }

            if(shownEnd.value.month > 2 && !isMobile || shownEnd.value.month > 1 && isMobile) {
                changeShownEnd(shownEnd.value.month - addMonth, shownEnd.value.year);
            } else if(shownEnd.value.month === 2 && !isMobile || shownEnd.value.month === 1 && isMobile) {
                changeShownEnd(12, shownEnd.value.year - 1);
            } else {
                changeShownEnd(11, shownEnd.value.year - 1);
            }

            isLoading.value = true;
            fetchDates(false);

        };

        const nextShownCalendar = () => {
            const addMonth = isMobile ? 1 : 2;

            if(shownStart.value.month < 11 && !isMobile || shownStart.value.month < 12 && isMobile) {
                changeShownStart(shownStart.value.month + addMonth, shownStart.value.year);
            } else if(shownStart.value.month === 11 && !isMobile || shownStart.value.month === 12 && isMobile) {
                changeShownStart(1, shownStart.value.year + 1);
            } else {
                changeShownStart(2, shownStart.value.year + 1);
            }

            if(shownEnd.value.month < 11 && !isMobile || shownEnd.value.month < 12 && isMobile) {
                changeShownEnd(shownEnd.value.month + addMonth, shownEnd.value.year);
            } else if(shownEnd.value.month === 11 && !isMobile || shownEnd.value.month === 12 && isMobile) {
                changeShownEnd(1, shownEnd.value.year + 1);
            } else {
                changeShownEnd(2, shownEnd.value.year + 1);
            }

            isLoading.value = true;
            // props.type === 'pricecalculator' ? refetchDates() : fetchDates(false);
            fetchDates(false);
        }

        const changeSelectedDates = (date: string) => {
            if(isUndefined(props.selectedDateStart) && !isActivity) { //don't disable previous dates for activities
                emit('changeDateStart', date);
            } else if(props.selectedDateStart > date) {
                emit('changeDateStart', date);
                emit('changeDateEnd', "");
            } else if(props.selectedDateStart < date && isUndefined(props.selectedDateEnd)) {
                emit('changeDateEnd', date);
            } else if(!isUndefined(props.selectedDateStart) && !isUndefined(props.selectedDateEnd)) {
                emit('changeDateStart', date);
                emit('changeDateEnd', "");
            }
            if(calendarConfig.value.minSelectableDates === 1 && calendarConfig.value.maxSelectableDates === 1) {
                emit('changeDateEnd', date);
            } 
        }

        //is clickable but looks disabled (needed so user can deselect date)
        const checkIfFakeDisabled = (date: string) => {
            let bool = false;
            if(!isUndefined(props.selectedDateStart) && props.selectedDateStart > date) {
                bool = true;
            }

            return bool;
        }

        const checkIfDisabled = (day: object) => {
            let bool = false;
            let date = day.date;
            if(!isUndefined(props.selectedDateStart) && isUndefined(props.selectedDateEnd) && date > addDaysISO(props.selectedDateStart, calendarConfig.value.maxSelectableDays)) {
                bool = true;
            } else if(!isUndefined(props.selectedDateStart) && isUndefined(props.selectedDateEnd) && date > props.selectedDateStart && date < addDaysISO(props.selectedDateStart, calendarConfig.value.minSelectableDays -1)) {
                bool = true;
            } else if(date < minDate.value || date > maxDate.value) {
                bool = true;
            } else if(day.disabled){
                bool = true;
            }

            return bool;
        }

        const checkIfPrevDisabled = (): boolean => {
            let monthNumber = getMonthNumberOfDate(minDate.value) +1;
            let yearNumber = +minDate.value.split("-")[0];
            return (shownStart.value.month === monthNumber && shownStart.value.year === yearNumber) || (shownEnd.value.month === monthNumber && shownEnd.value.year === yearNumber);
        }

        const checkIfNextDisabled = () => {
            let monthNumber = getMonthNumberOfDate(maxDate.value) +1;
            let yearNumber = +maxDate.value.split("-")[0];
            return (shownEnd.value.month === monthNumber && shownEnd.value.year === yearNumber) || (shownStart.value.month === monthNumber && shownStart.value.year === yearNumber);
        }

        if(props.type === 'pricecalculator') {
            watch(() => props.selectedPriceGroupIDs, () => {
                if(props.selectedPriceGroupIDs.length > 0) {
                    fetchDates(false);
                }
            })
        }

        onMounted(() => {
            fetchDates(true);
        });

        return {
            dateConfig,
            error,
            translate,
            weekdays,
            prevDayCount,
            getMonthName,
            changeShownEnd,
            changeShownStart,
            shownStart,
            shownEnd,
            changeSelectedDates,
            prevShownCalendar,
            nextShownCalendar,
            checkIfDisabled,
            checkIfFakeDisabled,
            checkIfPrevDisabled,
            checkIfNextDisabled,
            isUndefined,
            isMobile,
            isLoading
        }
    }
});
